import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import BodyText from '../../components/BodyText';
import TopBackNavBar from '../../components/BackNavBar';
import TextInput from '../../components/TextInput';
import { RedButton } from '../../components/Button';
import SelectInput from '../../components/SelectInput';
import validateCreditCardInfofrom, { creditCardInfoErrorsDefault } from './CreditCardValidations';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { CreditCardInfo } from '../../interfaces/CreditCardInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import parseQueryParams from '../../utils/parseQueryParams';
import makeSession from '../../utils/makeSession';
import ExternalLogin from '../../components/ExternalLogin/ExternalLogin';
import { FairBarInfo } from '../../interfaces/fairBar-info';
import FairInfoBar from '../../components/FairInfoBar';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import getFairInfo from '../../utils/getFairInfo';
import { Pages } from '../../App';
import { updateLogInData } from '../../DumbleData';
import { ConfigContext } from '../../providers/ConfigContext';
import getRecaptcha from '../../utils/getRecaptcha';
import BigNumber from 'bignumber.js';

interface Props {
	ccInfo: CreditCardInfo;
	setCCInfo: any;
	pInfo: PaymentInfo;
	payoffAmount: BigNumber;
	setPayoffAmount: React.Dispatch<React.SetStateAction<BigNumber>>;
	version: string;
	setLoggedIn: any;
	loggedIn: boolean;
	payoffFairId: number;
	setPayoffFairId: any;
	payoffFairInfo: FairBarInfo;
	setPayoffFairInfo: any;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const [mySession] = makeSession();
const [getFair] = getFairInfo();

const callSpacing = (input: string) => {
	let postSpacing = '';
	// only if they're accepted cards
	if (input[0] === '3') {
		const x = input.replace(/[^0-9]/gi, '').match(/(\d{0,4})(\d{0,6})(\d{0,5})/);
		postSpacing = (x?.[1] || '') + (x?.[2] ? ' ' + x[2] : '') + (x?.[3] ? ' ' + x[3] : '');
	} else {
		const x = input.replace(/[^0-9]/gi, '').match(/(\d{0,4})(\d{0,4})(\d{0,4})(\d{0,4})/);
		postSpacing = (x?.[1] || '') + (x?.[2] ? ' ' + x[2] : '') + (x?.[3] ? ' ' + x[3] : '') + (x?.[4] ? ' ' + x[4] : '');
	}
	return postSpacing;
};

const handleNameChange = (e: any, setter: any, maxChar: number) => {
	const inputValue = e.replace(/[^a-zA-Zà-žÀ-Ž\s'-]/g, '');
	if (inputValue.length <= maxChar) {
		setter(inputValue);
	}
};

const handleNumericChange = (e: any, setter: any, maxChar: number) => {
	const inputValue = e.replace(/[^0-9]/g, '');
	if (inputValue.length <= maxChar) {
		setter(inputValue);
	}
};

const blankError = 'Blank: This field is required.';
const validError = 'Invalid: Please enter a valid value.';
const expiredError = 'Please enter a valid expiration.';
const AMERICAN_EXPRESS = 'American Express';

const cvvType = (creditType: string) => {
	if (creditType === AMERICAN_EXPRESS) {
		return 4;
	}
	if (creditType !== '') {
		return 3;
	}
	return 4;
};

const cclength = (creditType: string) => {
	if (creditType === AMERICAN_EXPRESS) {
		return 15;
	}
	return 16;
};

const getCreditCardTypeAndCbsCT = (cardNumber: string) => {
	let creditType = '';
	let cbsCT = '';
	if (cardNumber.length > 0) {
		const firstNum = cardNumber[0];
		if (firstNum === '4') {
			creditType = 'Visa';
			cbsCT = '001';
		} else if (firstNum === '5' || firstNum === '2') {
			creditType = 'Mastercard';
			cbsCT = '002';
		} else if (firstNum === '6') {
			creditType = 'Discover';
			cbsCT = '004';
		} else if (firstNum === '3') {
			creditType = AMERICAN_EXPRESS;
			cbsCT = '003';
		}
	}
	return { creditType, cbsCT };
};

const handleFirstNameChange = (e: any, setFirstName: React.Dispatch<React.SetStateAction<string>>) => {
	handleNameChange(e, setFirstName, 30);
};
const handleLastNameChange = (e: any, setLastName: React.Dispatch<React.SetStateAction<string>>) => {
	handleNameChange(e, setLastName, 30);
};
const handleCardNumberChange = (e: any, setCardNumber: React.Dispatch<React.SetStateAction<string>>, creditType: string) => {
	handleNumericChange(e, setCardNumber, cclength(creditType));
};
const handleCvvChange = (e: any, setCvv: React.Dispatch<React.SetStateAction<string>>, creditType: string) => {
	handleNumericChange(e, setCvv, cvvType(creditType));
};

// Check if payment type is payoff
const checkForPayoffPayment = (setCurrentPage: React.Dispatch<React.SetStateAction<number>>, setPayoffFairId: any, setPayoffAmount: React.Dispatch<React.SetStateAction<BigNumber>>) => {
	const { type, fairId, amount } = parseQueryParams(window.location);

	if (type !== 'payoff' || !fairId || !amount) {
		setCurrentPage(Pages.Error);
		return;
	}

	// Handle case where fairId could be an array or a single value
	const parsedFairId = Array.isArray(fairId) ? parseFloat(fairId[0] || '0') : parseFloat(fairId || '0');
	const parsedAmount = Array.isArray(amount) ? parseFloat(amount[0] || '0') : parseFloat(amount || '0');

	if (isNaN(parsedFairId) || isNaN(parsedAmount)) {
		setCurrentPage(Pages.Error);
		return;
	}

	setPayoffFairId(parsedFairId);
	setPayoffAmount(BigNumber(parsedAmount));
};

const [getCaptcha] = getRecaptcha();

const CreditCardForm = ({
	ccInfo,
	setCCInfo,
	pInfo,
	payoffAmount,
	setPayoffAmount,
	version,
	setLoggedIn,
	loggedIn,
	payoffFairId,
	setPayoffFairId,
	payoffFairInfo,
	setPayoffFairInfo,
	setCurrentPage,
}: Props) => {
	const { apiBase, reCaptchaCheckboxKey, reCaptchaSiteKey } = useContext(ConfigContext);

	const [loading, setLoading] = React.useState(true);
	const [firstName, setFirstName] = React.useState('');
	const [lastName, setLastName] = React.useState('');
	const [cardNumber, setCardNumber] = React.useState('');
	const [expirationDateMonth, setExpirationDateMonth] = React.useState('');
	const [expirationDateYear, setExpirationDateYear] = React.useState('');
	const [cvv, setCvv] = React.useState('');
	const { creditType, cbsCT } = getCreditCardTypeAndCbsCT(cardNumber);
	const [creditCardInfoErrors, setCreditCardInfoErrors] = React.useState(creditCardInfoErrorsDefault);
	const [checkboxActive, setCheckboxActive] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);

	const currentDate = new Date();

	useEffect(() => {
		try {
			window.grecaptcha.enterprise.render('recaptchaContainer', {
				sitekey: reCaptchaCheckboxKey,
				action: 'CHECKOUT',
				callback: checkboxCallback,
			});
		} catch (error) {
			// reCAPTCHA may throw an error if the container has already been rendered
		}
	}, [loading]);

	const checkboxCallback = async (token: string) => {
		const { data } = await getCaptcha(token, apiBase, true);
		if (!data || data.status.toLowerCase() !== 'success') {
			return;
		}
		setCurrentPage(Pages.CreditCardReview);
	};

	useEffect(() => {
		if (version === 'sale') {
			setLoading(false);
		} else if (version === 'payoff') {
			checkForPayoffPayment(setCurrentPage, setPayoffFairId, setPayoffAmount);
		}
	}, [setCurrentPage, setPayoffAmount, setPayoffFairId, version]);

	useEffect(() => {
		const getFairInfo = async (fairId: any) => {
			const { data, error } = await getFair(apiBase, fairId);
			if (error) {
				setCurrentPage(Pages.Error);
			} else if (data) {
				setPayoffFairInfo(data);
			}
		};

		// Get session info
		const createSessionInfo = async (fairId: any) => {
			const { data, error } = await mySession(apiBase, fairId);
			if (error) {
				setLoggedIn(!(error.errorMessage && error.errorMessage === 'Not a valid session. Please make sure that a valid SCHL cookie is specified.'));
				updateLogInData();
			} else if (data) {
				setLoggedIn(true);
			}
		};

		if (payoffFairId !== 0) {
			createSessionInfo(payoffFairId).finally(() => {
				getFairInfo(payoffFairId).finally(() => {
					setLoading(false);
				});
			});
		}
	}, [apiBase, payoffFairId, payoffAmount, setCurrentPage, setLoggedIn, setPayoffFairInfo]);

	const handleSubmit = () => {
		setButtonLoading(true);
		const errors = validateCreditCardInfofrom({ firstName, lastName, cardNumber, expirationMonth: expirationDateMonth, expirationYear: expirationDateYear, cvv });
		setCreditCardInfoErrors(errors);
		if (Object.values(errors).every((x) => x === false)) {
			const tempCCInfo = {
				...ccInfo,
				firstName: firstName,
				lastName: lastName,
				cardNumber: cardNumber,
				cardExpirationMonth: expirationDateMonth,
				cardExpirationYear: expirationDateYear,
				cardCvv: cvv,
				cardType: creditType,
				cbsCT: cbsCT,
			};
			setCCInfo(tempCCInfo);
			window._satellite.track('credit-card-review-payment-button');
			window.grecaptcha.enterprise.ready(async () => {
				const token = await window.grecaptcha.enterprise.execute(reCaptchaSiteKey, { action: 'CHECKOUT' });
				const { data } = await getCaptcha(token, apiBase, false);
				if (!data) {
					return;
				}
				if (data.status.toLowerCase() !== 'success') {
					setCheckboxActive(true);
					return;
				}
				setCurrentPage(Pages.CreditCardReview);
			});
		} else {
			setButtonLoading(false);
		}
	};

	useEffect(() => {
		if (creditType !== '' && creditType !== AMERICAN_EXPRESS && cvv.length > 3) {
			setCvv(cvv.slice(0, 3));
		}
		if (cardNumber !== '' && creditType === AMERICAN_EXPRESS && cardNumber.length > 15) {
			setCardNumber(cardNumber.slice(0, 15));
		}
	}, [cardNumber, cvv]);

	return (
		<>
			{!loading && (
				<>
					{!loggedIn ? (
						<ExternalLogin fairId={payoffFairId.toString()} setLoggedIn={setLoggedIn} handleRedirect={window.location.href} />
					) : (
						<>
							{version === 'payoff' && (
								<>
									<LandingPageHeader />
									<FairInfoBar fairInfo={payoffFairInfo} />
								</>
							)}
							<TopBackNavBar setCurrentPage={setCurrentPage} title="Credit Card Payment" location={version === 'sale' ? Pages.PaymentMethod : undefined} />
							<CreditCardFormPage>
								<CalculationsDiv>
									{version === 'sale' && (
										<>
											<RowItemDiv>
												<ItemLabel>Item Total</ItemLabel>
												<ItemValue>{formatCurrencyNumber(pInfo.itemTotal.toNumber())}</ItemValue>
											</RowItemDiv>
											<HorizontalLine />
											<RowItemDiv>
												<ItemLabel>Tax</ItemLabel>
												<ItemValue>{formatCurrencyNumber(pInfo.tax.toNumber())}</ItemValue>
											</RowItemDiv>
											<HorizontalLine />
											<RowItemDiv>
												<ItemLabel>Amount Due</ItemLabel>
												<ItemValue>{formatCurrencyNumber(pInfo.amountDue.toNumber())}</ItemValue>
											</RowItemDiv>
										</>
									)}
									{version === 'payoff' && (
										<>
											<RowItemDiv>
												<ItemLabel>Amount Due</ItemLabel>
												<ItemValue>{formatCurrencyNumber(payoffAmount.toNumber())}</ItemValue>
											</RowItemDiv>
										</>
									)}
								</CalculationsDiv>
								<ContentWrapper>
									<CreditCardInfoDiv>
										<NameDiv>
											<TextInputWrapperBig>
												<TextInput label="First Name" value={firstName} onChange={(e) => handleFirstNameChange(e, setFirstName)} error={creditCardInfoErrors.firstName} errorText={blankError} />
											</TextInputWrapperBig>
											<TextInputWrapperBig>
												<TextInput label="Last Name" value={lastName} onChange={(e) => handleLastNameChange(e, setLastName)} error={creditCardInfoErrors.lastName} errorText={blankError} />
											</TextInputWrapperBig>
										</NameDiv>
										<TextInput
											label="Card Card Number"
											value={callSpacing(cardNumber)}
											onChange={(e) => handleCardNumberChange(e, setCardNumber, creditType)}
											error={creditCardInfoErrors.cardNumber || creditCardInfoErrors.cardNumberNotValid}
											errorText={creditCardInfoErrors.cardNumber ? blankError : validError}
										/>
										<LowerCreditCardDiv>
											<TextInputWrapperSmall>
												<SelectInput
													label={'Expiration'}
													optionChosen={expirationDateMonth}
													onChange={setExpirationDateMonth}
													options={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
													defaultText="Month"
													error={creditCardInfoErrors.expirationMonth}
													errorText={blankError}
												/>
											</TextInputWrapperSmall>
											<TextInputWrapperSmall>
												<SelectInput
													label={'\u00A0'}
													optionChosen={expirationDateYear}
													onChange={setExpirationDateYear}
													options={Array.from({ length: 20 }, (_, i) => (currentDate.getFullYear() + i).toString())}
													defaultText="Year"
													error={creditCardInfoErrors.expirationYear}
													errorText={blankError}
												/>
											</TextInputWrapperSmall>
											<TextInputWrapperSmall>
												<TextInput
													label="Security Code"
													value={cvv}
													onChange={(e) => handleCvvChange(e, setCvv, creditType)}
													error={creditCardInfoErrors.cvv || creditCardInfoErrors.cvvNotValid}
													errorText={creditCardInfoErrors.cvv ? blankError : validError}
												/>
											</TextInputWrapperSmall>
										</LowerCreditCardDiv>
									</CreditCardInfoDiv>
									<RecaptchaContainer $checkboxActive={checkboxActive} className="g-recaptcha" id="recaptchaContainer"></RecaptchaContainer>
									<StyledRedButton handleClick={handleSubmit} disabled={buttonLoading}>
										Review Payment
									</StyledRedButton>
									{creditCardInfoErrors.cardExpired && <ErrorText>{expiredError}</ErrorText>}
								</ContentWrapper>
							</CreditCardFormPage>
						</>
					)}
				</>
			)}
		</>
	);
};

const RecaptchaContainer = styled.div<{ $checkboxActive?: boolean }>`
	margin-top: 40px;
	display: ${(props) => (props.$checkboxActive ? 'block' : 'none')};
`;

const ErrorText = styled.p`
	color: #e81111;
	font-weight: 500;
	font-size: 13px;
	font-height: 15px;
	margin: 0;
`;

const CreditCardFormPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	padding: 0 20px;
	@media (max-width: 480px) {
		width: 100%;
	}
`;

const CalculationsDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	min-width: 280px;
	margin: 32px 0 40px 0;
	gap: 12px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const CreditCardInfoDiv = styled.div`
	display: inline-flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 25px;
	max-width: 440px;
	@media (max-width: 480px) {
		width: 100%;
	}
`;

const NameDiv = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	width: 100%;
	gap: 40px;
	@media (max-width: 480px) {
		flex-direction: column;
		gap: 25px;
	}
`;

const TextInputWrapperBig = styled.div`
	width: 200px;
	@media (max-width: 480px) {
		width: 100%;
	}
`;

const LowerCreditCardDiv = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 30px;
	@media (max-width: 480px) {
		gap: 16px;
	}
`;

const TextInputWrapperSmall = styled.div`
	flex: 1;
	min-width: 120px;
	@media (max-width: 480px) {
		flex: none;
		width: 120px;
		&:nth-child(1),
		&:nth-child(2) {
			width: calc(50% - 8px);
		}
		&:nth-child(3) {
			margin-top: 9px;
			width: 100%;
		}
	}
`;

const StyledRedButton = styled(RedButton)`
	margin: 45px 0 20px 0;
	width: 100%;
`;

export default CreditCardForm;
